import React, { useCallback, useEffect, useState } from "react";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { getPushBroadcasts } from "../../api/apiServiceFetch";
import { audienceLabel, formatCount, sexLabel } from "./broadcastOptions";

const statusColors = {
	pending: "#888",
	in_progress: "#0a84ff",
	completed: "#22c55e",
	cancelled: "#ef4444",
};

const statusLabels = {
	pending: "ждёт отправки",
	in_progress: "отправляется",
	completed: "завершена",
	cancelled: "отменена",
};

// Пока задание не разобрано, счётчики меняются каждые несколько секунд —
// список должен обновляться сам, иначе за ходом отправки не уследить.
const REFRESH_MS = 5000;

const isActive = (job) =>
	job?.status === "pending" || job?.status === "in_progress";

/**
 * История рассылок пушами. Уведомления уходят фоновым рассыльщиком, поэтому
 * задание сначала висит в очереди и лишь потом «утекает» в отправленные —
 * пропущенные и сбойные показываем отдельно, иначе неудачи растворяются.
 */
const PushBroadcastsHistory = ({ reloadToken }) => {
	const [jobs, setJobs] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getPushBroadcasts(100, 0);
			setJobs(Array.isArray(data) ? data : []);
			setError(null);
		} catch (e) {
			console.error("getPushBroadcasts failed:", e);
			setError(
				`Не удалось загрузить историю уведомлений: ${e?.message || "запрос не удался"}`,
			);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		load();
	}, [load, reloadToken]);

	const hasActive = jobs.some(isActive);

	useEffect(() => {
		if (!hasActive) return undefined;
		const timer = setInterval(load, REFRESH_MS);
		return () => clearInterval(timer);
	}, [hasActive, load]);

	return (
		<div className="broadcasts-page__section">
			<div className="broadcasts-page__section-head">
				<h2>История: уведомления (пуши)</h2>
				<Button text="Обновить" onClick={load} disabled={isLoading} />
			</div>

			{error && (
				<p
					className="broadcasts-page__status is-error"
					style={{ marginTop: 0, marginBottom: 12 }}
				>
					{error}
				</p>
			)}

			<p
				className="broadcasts-page__hint"
				style={{ maxWidth: 880 }}
			>
				«Пропущено» — это не сбой: столько людей к моменту отправки остались
				без живой подписки. Выключенный тумблер уведомлений сюда не относится:
				объявления он не гасит. «Сбой» — уведомление не приняла Apple,
				Google или push-сервис браузера. Пробные отправки в список не попадают.
				Картинку из колонки «Картинка» видят Android и Chrome/Edge; на iPhone
				то же уведомление приходит с текстом, но без неё.
				{hasActive
					? " Есть незавершённые задания — список обновляется каждые 5 секунд."
					: ""}
			</p>

			<DataGrid
				dataSource={jobs}
				keyExpr="id"
				showBorders={true}
				rowAlternationEnabled={true}
			>
				<Paging defaultPageSize={10} />
				<Column dataField="id" caption="ID" width={70} />
				<Column
					dataField="audienceType"
					caption="Аудитория"
					width={190}
					calculateCellValue={(d) => audienceLabel(d.audienceType)}
				/>
				<Column
					dataField="sex"
					caption="Пол"
					width={110}
					calculateCellValue={(d) => sexLabel(d.sex)}
				/>
				<Column
					dataField="status"
					caption="Статус"
					width={140}
					cellRender={({ data }) => (
						<span
							style={{
								color: statusColors[data.status] ?? "#000",
								fontWeight: 600,
							}}
						>
							{statusLabels[data.status] ?? data.status}
						</span>
					)}
				/>
				{/* Размер аудитории и число уведомлений расходятся в разы, и это
				    должно быть видно и постфактум, а не только перед отправкой. */}
				<Column
					dataField="audienceTotal"
					caption="Аудитория"
					width={110}
					calculateCellValue={(d) => formatCount(Number(d.audienceTotal ?? 0))}
				/>
				<Column
					dataField="totalTargets"
					caption="Уведомлений"
					width={120}
					calculateCellValue={(d) => formatCount(Number(d.totalTargets ?? 0))}
				/>
				<Column
					dataField="mobileTargets"
					caption="Приложение"
					width={110}
				/>
				<Column dataField="webTargets" caption="Браузер" width={100} />
				<Column dataField="sent" caption="Отправлено" width={110} />
				<Column dataField="skipped" caption="Пропущено" width={110} />
				<Column
					dataField="failed"
					caption="Сбой"
					width={90}
					cellRender={({ data }) => (
						<span
							style={{
								color: Number(data.failed ?? 0) > 0 ? "#d5364e" : "#000",
								fontWeight: Number(data.failed ?? 0) > 0 ? 600 : 400,
							}}
						>
							{data.failed ?? 0}
						</span>
					)}
				/>
				<Column dataField="pending" caption="В очереди" width={110} />
				<Column
					dataField="appBroadcastId"
					caption="Рассылка в чате"
					width={140}
					calculateCellValue={(d) =>
						d.appBroadcastId ? `№${d.appBroadcastId}` : "сам по себе"
					}
				/>
				<Column
					dataField="createdAt"
					caption="Создано"
					dataType="datetime"
					format="dd.MM.yyyy HH:mm"
					width={160}
				/>
				<Column dataField="title" caption="Заголовок" width={140} />
				{/* Картинка приходит ссылкой: постфактум важно понимать, ушла
				    рассылка с ней или без — «на айфоне картинки не было» иначе
				    не с чем сопоставить. */}
				<Column
					dataField="imageUrl"
					caption="Картинка"
					width={110}
					cellRender={({ data }) =>
						data.imageUrl ? (
							<a href={data.imageUrl} target="_blank" rel="noreferrer">
								есть
							</a>
						) : (
							<span style={{ color: "#888" }}>нет</span>
						)
					}
				/>
				<Column dataField="body" caption="Текст" />
			</DataGrid>
		</div>
	);
};

export default PushBroadcastsHistory;
